/**
 * useCameraSettings Hook
 * Manages camera settings state and persistence
 * Single Responsibility: State management + persistence
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { storage } from '../../services/StorageService';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { 
  CameraSettings, 
  CameraProfile, 
  CameraProfileMap, 
  DEFAULT_CAMERA_SETTINGS,
  validateCameraSettings,
  PRESET_CAMERA_PROFILES
} from './CameraTypes';
import { CameraManager } from './CameraManager';

export interface UseCameraSettingsOptions {
  cameraManager: CameraManager | null;
}

export interface UseCameraSettingsReturn {
  // Current settings
  settings: CameraSettings;
  updateSetting: <K extends keyof CameraSettings>(key: K, value: CameraSettings[K]) => void;
  resetSettings: () => void;
  
  // Profiles
  profiles: CameraProfileMap;
  currentProfileName: string;
  setCurrentProfileName: (name: string) => void;
  
  // Profile CRUD
  saveProfile: (name: string) => boolean;
  loadProfile: (name: string) => boolean;
  deleteProfile: (name: string) => boolean;
  
  // Actions
  applySettings: (newSettings: CameraSettings) => void;
  exportSettings: () => void;
  
  // Debug
  lastAction: string;
}

export function useCameraSettings(options: UseCameraSettingsOptions): UseCameraSettingsReturn {
  const { cameraManager } = options;
  
  // Settings state
  const [settings, setSettings] = useState<CameraSettings>(DEFAULT_CAMERA_SETTINGS);
  const [profiles, setProfiles] = useState<CameraProfileMap>({});
  const [currentProfileName, setCurrentProfileName] = useState('');
  const [lastAction, setLastAction] = useState('Initializing...');
  const isInitialized = useRef(false);

  // Load profiles from storage on mount (runs once)
  useEffect(() => {
    if (isInitialized.current) return;
    
    const savedProfiles = storage.getObject<CameraProfileMap>('helix_camera_profiles');
    
    // Merge presets with saved profiles (presets first, user saves override)
    const mergedProfiles: CameraProfileMap = {
      ...PRESET_CAMERA_PROFILES,
      ...(savedProfiles || {})
    };
    
    setProfiles(mergedProfiles);
    
    const presetCount = Object.keys(PRESET_CAMERA_PROFILES).length;
    const savedCount = savedProfiles ? Object.keys(savedProfiles).length : 0;
    
    if (savedCount > 0) {
      setLastAction(`Loaded ${presetCount} presets + ${savedCount} custom profiles`);
    } else {
      setLastAction(`Loaded ${presetCount} preset profiles (MapleStory, Cinematic, etc.)`);
    }
    
    isInitialized.current = true;
  }, []);

  // Sync with CameraManager when it becomes available
  useEffect(() => {
    if (cameraManager) {
      const currentZoom = cameraManager.getZoom();
      setSettings(prev => ({ ...prev, zoom: currentZoom }));
      setLastAction(`Camera connected. Zoom: ${currentZoom.toFixed(2)}x`);
    }
  }, [cameraManager]);

  // Apply settings to CameraManager
  const applySettings = useCallback((newSettings: CameraSettings) => {
    if (!cameraManager) {
      setLastAction('ERROR: CameraManager not available');
      return;
    }

    // Update min/max zoom bounds FIRST (before applying zoom)
    cameraManager.setConfig({ 
      minZoom: newSettings.minZoom,
      maxZoom: newSettings.maxZoom 
    });
    
    // Apply zoom (now uses updated bounds)
    cameraManager.setZoom(newSettings.zoom);
    
    // Apply follow speed (followLerp)
    cameraManager.setConfig({ followLerp: newSettings.followSpeed });
    
    // Apply deadzone
    cameraManager.setDeadzone?.(newSettings.deadzoneWidth, newSettings.deadzoneHeight);
    
    // Emit event for other systems
    EventBus.getInstance().emit(EVENTS.CAMERA_SETTINGS_CHANGED, newSettings);
    
    setLastAction(`Applied: zoom=${newSettings.zoom.toFixed(2)}x, speed=${(newSettings.followSpeed * 100).toFixed(0)}%`);
  }, [cameraManager]);

  // Update a single setting (hot apply)
  const updateSetting = useCallback(<K extends keyof CameraSettings>(
    key: K, 
    value: CameraSettings[K]
  ) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      applySettings(newSettings);
      return newSettings;
    });
  }, [applySettings]);

  // Reset to defaults
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_CAMERA_SETTINGS);
    applySettings(DEFAULT_CAMERA_SETTINGS);
    setCurrentProfileName('');
    setLastAction('Reset to defaults');
  }, [applySettings]);

  // Save current settings as a named profile
  const saveProfile = useCallback((name: string): boolean => {
    if (!name.trim()) {
      setLastAction('ERROR: Profile name required');
      return false;
    }

    const profile: CameraProfile = {
      name: name.trim(),
      settings: { ...settings },
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    const newProfiles = {
      ...profiles,
      [name.trim()]: profile
    };

    setProfiles(newProfiles);
    storage.setObject('helix_camera_profiles', newProfiles);
    setCurrentProfileName(name.trim());
    setLastAction(`Saved profile: "${name.trim()}"`);
    return true;
  }, [profiles, settings]);

  // Load a profile by name
  const loadProfile = useCallback((name: string): boolean => {
    const profile = profiles[name];
    if (!profile) {
      setLastAction(`ERROR: Profile "${name}" not found`);
      return false;
    }

    const validatedSettings = validateCameraSettings(profile.settings);
    setSettings(validatedSettings);
    applySettings(validatedSettings);
    setCurrentProfileName(name);
    setLastAction(`Loaded profile: "${name}"`);
    return true;
  }, [profiles, applySettings]);

  // Delete a profile
  const deleteProfile = useCallback((name: string): boolean => {
    if (!profiles[name]) {
      setLastAction(`ERROR: Profile "${name}" not found`);
      return false;
    }

    const newProfiles = { ...profiles };
    delete newProfiles[name];
    
    setProfiles(newProfiles);
    storage.setObject('helix_camera_profiles', newProfiles);
    
    if (currentProfileName === name) {
      setCurrentProfileName('');
    }
    
    setLastAction(`Deleted profile: "${name}"`);
    return true;
  }, [profiles, currentProfileName]);

  // Export settings to console
  const exportSettings = useCallback(() => {
    const exportData = {
      currentSettings: settings,
      profiles: profiles,
      timestamp: new Date().toISOString()
    };
    
    console.log('[CameraSettings] Export:', exportData);
    setLastAction('Settings exported to console (F12)');
    return exportData;
  }, [settings, profiles]);

  return {
    settings,
    updateSetting,
    resetSettings,
    profiles,
    currentProfileName,
    setCurrentProfileName,
    saveProfile,
    loadProfile,
    deleteProfile,
    applySettings,
    exportSettings,
    lastAction
  };
}
